import { css } from '@emotion/core';
import { theme } from '@weave/theme';
import React, { FC, Fragment, useMemo } from 'react';
import { Text } from '@weave/design-system';
import { PortFieldValue } from './PortFieldValue';
import { PortingAuditHistoryRecord } from '@weave/schema-gen-ts/dist/schemas/phone/porting/porting-data/v1/porting_data_service.pb';
import {
  getPortAuditRecordKeyData,
  getPortAuditHistoryDiff,
} from '../utils/porting-utils';

interface PortDetailCompareProps {
  newPortingData?: PortingAuditHistoryRecord;
  oldPortingData?: PortingAuditHistoryRecord;
}
export const PortDetailCompare: FC<PortDetailCompareProps> = ({
  newPortingData = {},
  oldPortingData = {},
}) => {
  const { changedFields, unchangedFields } = useMemo(() => {
    const diffKeys = getPortAuditHistoryDiff(newPortingData, oldPortingData);
    const unchangedFields = Object.entries(oldPortingData)
      .filter(
        ([key, value]) =>
          !diffKeys.includes(key as keyof PortingAuditHistoryRecord) &&
          value !== undefined
      )
      .map(([key, value]) => ({
        ...getPortAuditRecordKeyData(key as keyof PortingAuditHistoryRecord),
        value,
      }))
      .filter(({ isPrivate }) => !isPrivate);

    const changedFields = diffKeys
      .map((key) => ({
        ...getPortAuditRecordKeyData(key as keyof PortingAuditHistoryRecord),
        newValue: newPortingData[key],
        oldValue: oldPortingData[key],
      }))
      .filter(({ isPrivate }) => !isPrivate);

    return {
      unchangedFields,
      changedFields,
    };
  }, [newPortingData, oldPortingData]);

  return (
    <section css={cardContainerStyles}>
      {changedFields.length > 0 && (
        <div css={subSectionStyles}>
          <Text size="large" weight="bold" css={{ margin: 0 }}>
            Updated Fields
          </Text>
          <div css={contentCardStyles}>
            {changedFields.map(({ label, type, newValue, oldValue }) => (
              <Fragment key={label}>
                <Text size="large" color="light" css={{ margin: 0 }}>
                  {label}
                </Text>
                <PortFieldValue.Diff
                  oldValue={oldValue}
                  newValue={newValue}
                  type={type}
                />
              </Fragment>
            ))}
          </div>
        </div>
      )}

      {unchangedFields.length > 0 && (
        <div css={subSectionStyles}>
          <Text size="large" weight="bold" css={{ margin: 0 }}>
            Other Fields
          </Text>
          <Text size="medium" color="light" css={{ margin: 0 }}>
            These are the values at the time this edit was made.
          </Text>
          <div css={contentCardStyles}>
            {unchangedFields.map(({ label, value, type }) => (
              <Fragment key={label}>
                <Text size="large" color="light" css={{ margin: 0 }}>
                  {label}
                </Text>
                <PortFieldValue.FieldValue type={type} value={value} />
              </Fragment>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

const cardContainerStyles = css({
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 16,
  padding: theme.spacing(2),
  border: `1px solid ${theme.colors.neutral20}`,
  backgroundColor: theme.colors.neutral5,
  gap: theme.spacing(3),
});

const subSectionStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
});

const contentCardStyles = css({
  display: 'grid',
  gridTemplateColumns: 'minmax(300px, auto) 1fr',
  columnGap: theme.spacing(2),
  rowGap: theme.spacing(1),
  borderRadius: theme.borderRadius.medium,
  border: `1px solid ${theme.colors.neutral20}`,
  backgroundColor: theme.colors.white,
  padding: theme.spacing(2),
});
